/**
 * ContaminationMetricsCard Component
 *
 * Displays contamination rate metrics and breakdown by type.
 * Shows health status indicators (<10% healthy, 10-15% warning, >15% alert).
 *
 * User Story: US-P2-008 - Contamination Management
 */

import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useContaminationRate,
  useContaminationBreakdown,
} from '@/hooks/useContamination'
import {
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react'

// Type colours for breakdown chart
const TYPE_COLOURS = {
  green_mold: 'bg-green-500',
  black_mold: 'bg-gray-700',
  cobweb: 'bg-slate-400',
  bacterial: 'bg-yellow-500',
  yeast: 'bg-amber-400',
  wet_spot: 'bg-blue-400',
  lipstick: 'bg-orange-500',
  unknown: 'bg-slate-300',
}

const TYPE_NAMES = {
  green_mold: 'Green Mold',
  black_mold: 'Black Mold',
  cobweb: 'Cobweb',
  bacterial: 'Bacterial',
  yeast: 'Yeast',
  wet_spot: 'Wet Spot',
  lipstick: 'Lipstick',
  unknown: 'Unknown',
}

/**
 * Get status indicator based on contamination rate
 */
function getStatusConfig(status, rate) {
  switch (status) {
    case 'healthy':
      return {
        label: 'Healthy',
        Icon: CheckCircle,
        colour: 'text-green-600',
        bgColour: 'bg-green-50',
        borderColour: 'border-green-200',
        description: 'Contamination rate is under control',
      }
    case 'warning':
      return {
        label: 'Warning',
        Icon: AlertCircle,
        colour: 'text-yellow-600',
        bgColour: 'bg-yellow-50',
        borderColour: 'border-yellow-200',
        description: 'Rate approaching threshold - review practices',
      }
    case 'alert':
      return {
        label: 'Alert',
        Icon: AlertTriangle,
        colour: 'text-red-600',
        bgColour: 'bg-red-50',
        borderColour: 'border-red-200',
        description: 'Rate exceeds threshold - action required',
      }
    default:
      return {
        label: 'No Data',
        Icon: Minus,
        colour: 'text-slate-500',
        bgColour: 'bg-slate-50',
        borderColour: 'border-slate-200',
        description: 'Not enough data to calculate rate',
      }
  }
}

/**
 * Format percentage
 */
function formatPercentage(value) {
  if (value === null || value === undefined) return '0%'
  return `${Math.round(value * 10) / 10}%`
}

export default function ContaminationMetricsCard({
  strainId = null,
  startDate = null,
  endDate = null,
  compact = false,
}) {
  const { metrics, loading: rateLoading } = useContaminationRate(strainId, startDate, endDate)
  const { breakdown, loading: breakdownLoading } = useContaminationBreakdown(startDate, endDate)

  const loading = rateLoading || breakdownLoading

  const statusConfig = useMemo(() => {
    if (!metrics) return getStatusConfig('healthy', 0)
    return getStatusConfig(metrics.status, metrics.contamination_rate)
  }, [metrics])

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-medium">
          <AlertTriangle className="h-4 w-4" />
          Contamination Metrics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Summary */}
        <div className={`p-3 rounded-lg border ${statusConfig.bgColour} ${statusConfig.borderColour}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <statusConfig.Icon className={`h-5 w-5 ${statusConfig.colour}`} />
              <span className={`font-medium ${statusConfig.colour}`}>
                {statusConfig.label}
              </span>
            </div>
            <Badge variant="outline" className={statusConfig.colour}>
              {formatPercentage(metrics?.contamination_rate || 0)} rate
            </Badge>
          </div>
          <p className="text-xs text-slate-600 mt-1">{statusConfig.description}</p>

          {/* Rate Progress Bar */}
          <div className="mt-2">
            <Progress
              value={Math.min(metrics?.contamination_rate || 0, 30)}
              max={30}
              className="h-2"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>0%</span>
              <span className="text-green-600">10%</span>
              <span className="text-yellow-600">15%</span>
              <span className="text-red-600">30%+</span>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className={compact ? 'space-y-3' : 'grid grid-cols-2 gap-4'}>
          <div className="space-y-1">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Containers</p>
            <p className="text-2xl font-semibold">{metrics?.total_containers || 0}</p>
            <p className="text-xs text-slate-500">in period</p>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Contaminated</p>
            <p className={`text-2xl font-semibold ${statusConfig.colour}`}>
              {metrics?.contaminated_count || 0}
            </p>
            <p className="text-xs text-slate-500">disposed or quarantined</p>
          </div>
        </div>

        {/* Breakdown by Type */}
        {breakdown.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">
              Breakdown by Type
            </p>
            <div className="space-y-2">
              {breakdown.slice(0, 5).map(item => (
                <div key={item.contamination_type} className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${TYPE_COLOURS[item.contamination_type] || TYPE_COLOURS.unknown}`} />
                  <span className="text-sm flex-1">{TYPE_NAMES[item.contamination_type] || item.contamination_type}</span>
                  <span className="text-sm font-medium">{item.event_count}</span>
                  <span className="text-xs text-slate-500">({formatPercentage(item.percentage)})</span>
                </div>
              ))}
            </div>

            {/* Stacked bar showing distribution */}
            <div className="flex h-2 rounded overflow-hidden mt-3">
              {breakdown.map(item => (
                <div
                  key={item.contamination_type}
                  className={TYPE_COLOURS[item.contamination_type] || TYPE_COLOURS.unknown}
                  style={{ width: `${item.percentage}%` }}
                  title={`${TYPE_NAMES[item.contamination_type]}: ${item.percentage}%`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Thresholds Reference */}
        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs text-slate-400">
            Thresholds: <span className="text-green-600">Healthy &lt;10%</span> |{' '}
            <span className="text-yellow-600">Warning 10-15%</span> |{' '}
            <span className="text-red-600">Alert &gt;15%</span>
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
